import React from "react";
import { Card, CardContent, Typography } from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

const TestimonialCard = ({ name, testimonial, stacked = false }) => {
  const fontFamily = '"Inter", -apple-system, BlinkMacSystemFont, sans-serif';

  const cardStyles = {
    width: "100%",
    maxWidth: stacked ? 480 : 420,
    margin: "0 auto",
    borderRadius: stacked ? "18px" : "18px",
    boxShadow: stacked
      ? "0 10px 28px rgba(117, 86, 115, 0.18)"
      : "0 6px 24px rgba(117, 86, 115, 0.12)",
    background: stacked
      ? "linear-gradient(160deg, rgba(255, 255, 255, 0.98) 0%, rgba(244, 240, 247, 0.96) 100%)"
      : "#ffffff",
  height: stacked ? "auto" : 620,
  minHeight: stacked ? "auto" : 620,
    border: stacked
      ? "1px solid rgba(117, 86, 115, 0.16)"
      : "1px solid rgba(117, 86, 115, 0.1)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  const contentStyles = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: stacked
      ? { xs: "40px 18px 20px 18px", sm: "44px 24px 26px 24px" }
      : "28px 24px 24px 24px",
    position: "relative",
    height: "100%",
  };

  const contentAreaStyles = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
    minHeight: stacked ? "auto" : 420,
  };

  const characters = testimonial.length;

  const baseFontSize = stacked
    ? characters < 420
      ? { xs: "16px", sm: "17px" }
      : { xs: "14.5px", sm: "15.5px" }
    : characters < 420
    ? { xs: "15.5px", md: "17px" }
    : { xs: "13.5px", md: "15px" };

  const textStyles = stacked
    ? {
        fontStyle: "italic",
        color: "#2d2d2d",
        fontSize: baseFontSize,
        lineHeight: 1.68,
        fontFamily,
        whiteSpace: "pre-line",
      }
    : {
        fontStyle: "italic",
        color: "#333",
        fontSize: baseFontSize,
  lineHeight: characters < 420 ? 1.74 : 1.62,
        fontFamily,
        display: "-webkit-box",
        WebkitLineClamp: 18,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      };

  const quoteIconStyles = stacked
    ? {
        fontSize: "2.4rem",
        color: "#755673",
        opacity: 0.12,
        position: "absolute",
        top: "-6px",
        left: "-6px",
        zIndex: 0,
      }
    : {
        fontSize: "2.8rem",
        color: "#755673",
        opacity: 0.15,
        position: "absolute",
        top: "-8px",
        left: "-8px",
        zIndex: 0,
      };

  const authorSectionStyles = {
    display: "flex",
    alignItems: stacked ? "flex-start" : "center",
    justifyContent: stacked ? "center" : "flex-start",
    marginTop: "auto",
    paddingTop: stacked ? "20px" : "24px",
    borderTop: "1px solid rgba(117, 86, 115, 0.12)",
    gap: stacked ? "14px" : 0,
    flexWrap: stacked ? "wrap" : "nowrap",
  };

  const avatarStyles = {
    width: stacked ? "44px" : "50px",
    height: stacked ? "44px" : "50px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #755673 0%, #9d7a9b 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: stacked ? "0" : "16px",
    color: "white",
    fontWeight: 600,
    fontSize: stacked ? "16px" : "18px",
    boxShadow: "0 2px 8px rgba(117, 86, 115, 0.3)",
  };

  return (
    <Card
      className="testimonial-card"
      sx={{
        ...cardStyles,
        '&:hover': stacked
          ? {
              transform: "translateY(-3px)",
              boxShadow: "0 16px 36px rgba(117, 86, 115, 0.2)",
            }
          : {
              transform: "translateY(-6px)",
              boxShadow: "0 12px 40px rgba(117, 86, 115, 0.2)",
            },
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #755673, #a68da6, #755673)",
        }}
      />

      <CardContent sx={contentStyles}>
        <div
          style={{
            display: "flex",
            gap: "2px",
            justifyContent: "center",
            marginBottom: stacked ? "8px" : "12px",
          }}
        >
          {[...Array(5)].map((_, i) => (
            <StarRoundedIcon
              key={i}
              sx={{
                fontSize: stacked ? 18 : 20,
                color: "#755673",
                opacity: 0.9,
              }}
            />
          ))}
        </div>
        <div style={contentAreaStyles}>
          <FormatQuoteIcon sx={quoteIconStyles} />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              textAlign: "center",
              position: "relative",
              zIndex: 1,
              padding: stacked ? "12px 0" : "20px 0",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                ...textStyles,
                fontWeight: 400,
                textAlign: "center",
                paddingTop: stacked ? 2 : 0,
              }}
            >
              {testimonial}
            </Typography>
          </div>
        </div>

        <div style={authorSectionStyles}>
          <div style={avatarStyles}>{name.charAt(0).toUpperCase()}</div>

          <div
            style={{
              flex: 1,
              textAlign: stacked ? "center" : "left",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: "#755673",
                fontSize: stacked ? "15px" : "16px",
                margin: stacked ? "0 auto 2px auto" : 0,
                fontFamily,
                lineHeight: 1.2,
              }}
            >
              {name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#666",
                fontSize: "13px",
                fontStyle: "italic",
                opacity: 0.8,
              }}
            >
              Verified Client
            </Typography>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TestimonialCard;
